import React, { useEffect, useRef, useState } from 'react';
import { Braintree, HostedField } from 'react-braintree-fields';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import SectionLoader from '../../Loader/SectionLoader';
import ModalButtons from '../../Modal/ModalButtons';
import { fetchClientToken } from '../../../store/actions/creditCard/creditCardActions';
import { FETCH_CLIENT_TOKEN_LOADING } from '../../../store/actions/creditCard/creditCardActionConstants';
import { selectGetClientToken } from '../../../store/selectors/creditCardSelectors';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import {
  InputError,
  InputLabel,
} from '../../InputFields/Components/InputComponents';
import { uOutlineNone } from '../../../assets/styles/utility';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow, pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${pxToRem(12)};
`;

const StepNumberNote = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
`;

const StepTitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
`;

const BraintreeFieldWrapper = styled.div`
  margin-bottom: ${({ withoutBottomMargin }) =>
    !withoutBottomMargin && `${pxToRem(12)}`};
  iframe {
    float: none !important;
  }

  .braintree-hosted-field {
    border-radius: ${variables.borderRadius.borderRadius};
    ${uOutlineNone};
    font-size: ${pxToRem(14)};
    font-weight: 400;
    line-height: 1.4;
    height: 38px;
    padding: 0 ${pxToRem(16)};
    color: ${themeColors.colorTextPrimary};
    background-color: ${themeColors.colorInputBg};
    width: 100%;
    border: ${({ error }) =>
      error ? `1px solid ${themeColors.colorError}` : 'transparent'};
  }

  .braintree-hosted-fields-focused {
    border: ${({ error }) =>
      error
        ? `1px solid ${themeColors.colorError}`
        : `1px solid ${themeColors.colorInputBorderFocused}`};
  }

  ${InputLabel} {
    font-size: 14px;
    color: ${themeColors.colorTextSecondary};
    font-weight: 500;

    ${mediaBelow(variables.breakpoints.bpXl)} {
      font-size: 14px;
    }
  }
  &::placeholder {
    font-size: 14px;
    color: ${themeColors.colorTextSecondary};
    font-family: ${variables.family.regular} !important;
    font-weight: 400;
  }
`;

const CardDetailsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${pxToRem(12)};

  ${InputLabel} {
    font-size: 14px;
    color: ${themeColors.colorTextSecondary};
    font-weight: 500;

    ${mediaBelow(variables.breakpoints.bpXl)} {
      font-size: 14px;
    }
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-wrap: nowrap;
    justify-content: space-between;
    gap: ${pxToRem(8)};
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  &:nth-child(1),
  &:nth-child(2) {
    flex: 0 1 calc(25% - 6px);
  }

  &:nth-child(3) {
    flex: 0 1 calc(50% - 12px); /* CVV takes the other half */
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    &:nth-child(1),
    &:nth-child(2),
    &:nth-child(3) {
      flex: 1 1 33.33%;
      min-width: 0;
    }
  }

  ${BraintreeFieldWrapper} {
    ${mediaBelow(variables.breakpoints.bpMd)} {
      width: 100%;
    }
  }
`;

const ButtonsWrapper = styled.div`
  margin-top: auto;
`;

const EnterCardDetailsStep = ({
  onButtonClick,
  goToInitialModal,
  handleNext,
  setWizardData,
  postPaidFlow,
  steps,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tokenize, setTokenizeFunc] = useState();
  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setError] = useState();
  const [cardNumberError, setCardNumberError] = useState('');
  const [cardholderNameError, setCardholderNameError] = useState('');
  const [expirationMonthError, setExpirationMonthError] = useState('');
  const [expirationYearError, setExpirationYearError] = useState('');
  const [cvvError, setCvvError] = useState('');
  // const [cardType, setCardType] = useState();
  const [isReady, setIsReady] = useState(false);

  const accountUid = useSelector(getCurrentAccountUid);
  const clientToken = useSelector(selectGetClientToken);

  const cardNumberField = useRef();
  const cardholderNameField = useRef();
  const expirationMonthField = useRef();
  const expirationYearField = useRef();
  const cvvField = useRef();

  const isFetchingClientToken = useSelector(
    selectIsLoadingByActionType(FETCH_CLIENT_TOKEN_LOADING),
  );

  // const onCardTypeChange = ({ cards }) => {
  //   if (cards.length === 1) {
  //     const [card] = cards;

  //     setCardType(card.type);
  //   } else {
  //     setCardType('');
  //   }
  // };

  useEffect(() => {
    dispatch(fetchClientToken({ accountUid }));
  }, [dispatch, accountUid]);

  const onAuthorizationSuccess = () => cardNumberField?.current?.focus();
  const getNonceToken = () => {
    try {
      tokenize()
        .then((response) => {
          setWizardData({
            Bin: response.details.bin,
            Last4Digits: response.details.lastFour,
            ExpirationDate: `${response.details.expirationMonth}/${response.details.expirationYear}`,
            NameOnCard: response.details.cardholderName,
            Nonce: response.nonce,
            CreditCardType: response.details.cardType.replace(' ', ''),
          });
        })
        .then(handleNext);
    } catch (error) {
      setError(error);
    }
  };

  return (
    <SectionLoader isLoading={isFetchingClientToken && !isReady}>
      <StepWrapper>
        <StepNumberNote>
          {t('creditCard.stepNumber', {
            currentStepNumber: 1,
            numberOfSteps: steps.length,
          })}
        </StepNumberNote>
        <StepTitle>{t('creditCard.creditCardStep')}</StepTitle>
      </StepWrapper>

      {/* {clientToken && ( */}
      <>
        <Braintree
          authorization={clientToken || ''}
          onAuthorizationSuccess={() => {
            onAuthorizationSuccess();
            setIsReady(true);
          }}
          onReady={() => setIsReady(true)}
          getTokenRef={(ref) => setTokenizeFunc(() => ref)}
          // onCardTypeChange={onCardTypeChange}
          onValidityChange={(event) => {
            const {
              number,
              cardholderName,
              expirationMonth,
              expirationYear,
              cvv,
            } = event.fields;

            setIsFormValid(
              number.isValid &&
                cardholderName.isValid &&
                expirationMonth.isValid &&
                expirationYear.isValid &&
                cvv.isValid,
            );
          }}
          styles={{
            input: {
              'font-size': '14px',
              color: themeColors.colorTextPrimary,
              'font-family': 'Instrument Sans Medium, Arial, sans-serif',
              position: 'static',
              'font-weight': '500',
            },
            ':focus': {
              color: themeColors.colorTextPrimary,
            },
            '.number': {
              'font-size': '14px',
            },
            select: {
              'font-size': '14px',
              color: themeColors.colorTextPrimary,
            },
            '::placeholder': {
              'font-size': '14px',
              color: themeColors.colorSuccess,
              'font-family': 'Instrument Sans Regular, Arial, sans-serif',
              'font-weight': '400',
            },
            '::-webkit-input-placeholder': {
              'font-size': '14px',
              color: themeColors.colorSuccess,
              'font-family': 'Instrument Sans Regular, Arial, sans-serif',
              'font-weight': '400',
            },
            'input::placeholder': {
              'font-size': '14px',
              color: themeColors.colorTextSecondary,
              'font-family': 'Instrument Sans Regular, Arial, sans-serif',
              'font-weight': '400',
            },
            '::-moz-placeholder': {
              'font-size': '14px',
              color: themeColors.colorTextSecondary,
              'font-family': 'Instrument Sans Regular, Arial, sans-serif',
              'font-weight': '400',
            },
            ':-ms-input-placeholder': {
              'font-size': '14px',
              color: themeColors.colorTextSecondary,
              'font-family': 'Instrument Sans Regular, Arial, sans-serif',
              'font-weight': '400',
            },
          }}
          fontSrc={`
              @font-face {
                font-family: 'Instrument Sans Regular';
                src: url('../../../assets/fonts/GoldSilver/InstrumentSans-Regular.woff2') format('woff2');
                font-weight: 400;
                font-style: normal;
              }
              @font-face {
                font-family: 'Instrument Sans Medium';
                src: url('../../../assets/fonts/GoldSilver/InstrumentSans-Medium.woff2') format('woff2');
                font-weight: 500;
                font-style: normal;
              }
              
            `}
        >
          <BraintreeFieldWrapper error={cardNumberError}>
            <InputLabel>{t('creditCard.fields.cardNumberStar')}</InputLabel>
            <HostedField
              onError={cardNumberError}
              type="number"
              ref={cardNumberField}
              autocomplete="cc-number"
              placeholder={t('creditCard.fields.cardNumberPlaceholder')}
              onEmpty={() =>
                setCardNumberError(
                  t(
                    'validationSchema.addCreditCardValidation.cardNumberRequired',
                  ),
                )
              }
              onBlur={(event) => {
                if (event.isEmpty) {
                  setCardNumberError(
                    t(
                      'validationSchema.addCreditCardValidation.cardNumberRequired',
                    ),
                  );
                } else if (!event.isEmpty && !event.isValid) {
                  setCardNumberError(
                    t(
                      'validationSchema.addCreditCardValidation.cardNumberIsNotValid',
                    ),
                  );
                }
              }}
              onValidityChange={(event) => {
                if (!event.isValid) {
                  setCardNumberError(
                    t(
                      'validationSchema.addCreditCardValidation.cardNumberIsNotValid',
                    ),
                  );
                } else if (event.isValid) {
                  setCardNumberError('');
                }
              }}
            />

            {cardNumberError && <InputError>{cardNumberError}</InputError>}
            {/* <CardTypeText>
                Card type: {cardType ? cardType.toUpperCase() : '-'}
              </CardTypeText> */}
          </BraintreeFieldWrapper>
          <BraintreeFieldWrapper error={cardholderNameError}>
            <InputLabel>{t('creditCard.fields.nameOnCardStar')}</InputLabel>

            <HostedField
              type="cardholderName"
              ref={cardholderNameField}
              autocomplete="cc-name"
              placeholder={t('creditCard.fields.nameOnCardPlaceholder')}
              onBlur={(event) => {
                if (event.isEmpty) {
                  setCardholderNameError(
                    t(
                      'validationSchema.addCreditCardValidation.nameOnCardIsRequired',
                    ),
                  );
                }
              }}
              onValidityChange={(event) => {
                if (!event.isValid) {
                  setCardholderNameError(
                    t(
                      'validationSchema.addCreditCardValidation.nameOnCardIsRequired',
                    ),
                  );
                } else if (event.isValid) {
                  setCardholderNameError('');
                }
              }}
            />

            {cardholderNameError && (
              <InputError>{cardholderNameError}</InputError>
            )}
          </BraintreeFieldWrapper>
          <CardDetailsRow>
            <FieldGroup>
              <InputLabel>{t('creditCard.fields.expiresStar')}</InputLabel>
              {/* <InRowWrapper withBottomMargin> */}
              <BraintreeFieldWrapper
                error={expirationMonthError}
                withoutBottomMargin
              >
                <HostedField
                  type="expirationMonth"
                  ref={expirationMonthField}
                  autocomplete="cc-exp-month"
                  placeholder="MM"
                  onBlur={(event) => {
                    if (event.isEmpty) {
                      setExpirationMonthError(
                        t(
                          'validationSchema.addCreditCardValidation.expirationMonthAndYearIsRequired',
                        ),
                      );
                    }
                  }}
                  onValidityChange={(event) => {
                    if (!event.isValid) {
                      setExpirationMonthError(
                        t(
                          event.isEmpty
                            ? 'validationSchema.addCreditCardValidation.expirationMonthAndYearIsRequired'
                            : 'validationSchema.addCreditCardValidation.expirationMonthAndYearIsNotValid',
                        ),
                      );
                    } else if (event.isValid) {
                      setExpirationMonthError('');
                    }
                  }}
                />
              </BraintreeFieldWrapper>
            </FieldGroup>
            <FieldGroup>
              <InputLabel>&nbsp;</InputLabel>
              {/* <FieldSeparator>/</FieldSeparator> */}
              <BraintreeFieldWrapper
                error={expirationYearError}
                withoutBottomMargin
              >
                <HostedField
                  type="expirationYear"
                  ref={expirationYearField}
                  autocomplete="cc-exp-year"
                  placeholder="YYYY"
                  onBlur={(event) => {
                    if (event.isEmpty) {
                      setExpirationYearError(
                        t(
                          'validationSchema.addCreditCardValidation.expirationMonthAndYearIsRequired',
                        ),
                      );
                    }
                  }}
                  onValidityChange={(event) => {
                    if (!event.isValid) {
                      setExpirationYearError(
                        t(
                          event.isEmpty
                            ? 'validationSchema.addCreditCardValidation.expirationMonthAndYearIsRequired'
                            : 'validationSchema.addCreditCardValidation.expirationMonthAndYearIsNotValid',
                        ),
                      );
                    } else if (event.isValid) {
                      setExpirationYearError('');
                    }
                  }}
                />
              </BraintreeFieldWrapper>
              {/* </InRowWrapper> */}
              {(expirationMonthError || expirationYearError) && (
                <InputError>
                  {expirationMonthError || expirationYearError}
                </InputError>
              )}
            </FieldGroup>
            <FieldGroup>
              <BraintreeFieldWrapper error={cvvError}>
                <InputLabel>
                  {t('creditCard.fields.securityCodeStar')}
                </InputLabel>
                {/* <FlexRowWrapper> */}
                {/* <InRowWrapper> */}
                <HostedField
                  type="cvv"
                  ref={cvvField}
                  autocomplete="cc-exp-year"
                  placeholder={t('creditCard.fields.securityCodePlaceholder')}
                  onEmpty={() =>
                    setCvvError(
                      t(
                        'validationSchema.addCreditCardValidation.securityCodeIsRequired',
                      ),
                    )
                  }
                  onBlur={(event) => {
                    if (event.isEmpty) {
                      setCvvError(
                        t(
                          'validationSchema.addCreditCardValidation.securityCodeIsRequired',
                        ),
                      );
                    } else if (!event.isEmpty && !event.isValid) {
                      setCvvError(
                        t(
                          'validationSchema.addCreditCardValidation.securityCodeIsNotValid',
                        ),
                      );
                    }
                  }}
                  onValidityChange={(event) => {
                    if (!event.isValid && !event.isEmpty) {
                      setCvvError(
                        t(
                          'validationSchema.addCreditCardValidation.securityCodeIsNotValid',
                        ),
                      );
                    } else if (event.isValid) {
                      setCvvError('');
                    }
                  }}
                />
                {/* </InRowWrapper> */}
                {/* <IconWrapper onClick={() => {}}>
                  <QuestionMark />
                </IconWrapper>
              </FlexRowWrapper> */}
                {cvvError && <InputError>{cvvError}</InputError>}
              </BraintreeFieldWrapper>
            </FieldGroup>
          </CardDetailsRow>
          {error && (
            <InputError>{t('apiErrors.SomethingWentWrong')}</InputError>
          )}
        </Braintree>
        <ButtonsWrapper>
          <ModalButtons
            isHorizontal
            marginTop
            secondaryButtonProps={{
              onClick: postPaidFlow ? goToInitialModal : onButtonClick,
              label: t('common.cancel'),
            }}
            primaryButtonProps={{
              type: 'submit',
              label: t('common.next'),
              onClick: getNonceToken,
              disabled: !isFormValid,
            }}
          />
        </ButtonsWrapper>
      </>
      {/* )} */}
      {/* <NextStepNote>{t('creditCard.nextStepText')}</NextStepNote> */}
    </SectionLoader>
  );
};

EnterCardDetailsStep.propTypes = {
  onButtonClick: PropTypes.func,
  handleNext: PropTypes.func,
  setWizardData: PropTypes.func,
  postPaidFlow: PropTypes.bool,
  goToInitialModal: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      percentage: PropTypes.number,
    }),
  ),
};

export default EnterCardDetailsStep;
